import { useCallback, useEffect, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useAuth } from "../../../context/AuthContext";

type BiometryRow = {
  request_id: string;
  request_points: number | null;
  status: string;
  document_type: string;
  request_created_at: string | null;
  request_biometry_url: string | null;
};

type Busy = null | { kind: "sync" | "accept"; requestId: string };

type Props = {
  leadUlid: string;
  onAfterChange: () => void;
};

const STATUS_STYLES: Record<string, { bg: string; fg: string; label: string }> = {
  CHECKED: { bg: "#d4edda", fg: "#155724", label: "Done by customer" },
  ACCEPTED: { bg: "#fff3cd", fg: "#856404", label: "Manually accepted" },
  CREATED: { bg: "#e7f1ff", fg: "#0a58ca", label: "Customer pending" },
};

const fmtDateTime = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function BiometryActions({ leadUlid, onAfterChange }: Props) {
  const { token } = useAuth();
  const [rows, setRows] = useState<BiometryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [feedback, setFeedback] = useState<
    | { kind: "success" | "error"; message: string }
    | null
  >(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await request<{ items: BiometryRow[] }>(
        `/api/admin/operations/leads/${leadUlid}/biometry`,
        { token },
      );
      setRows(data.items);
    } catch (err) {
      setError(err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [leadUlid, token]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (
    kind: "sync" | "accept",
    requestId: string,
    successMsg: string,
  ) => {
    if (busy) return;
    setBusy({ kind, requestId });
    setFeedback(null);
    try {
      await request<unknown>(
        `/api/admin/operations/leads/${leadUlid}/biometry/${requestId}/${kind}`,
        { method: "POST", token, body: {} },
      );
      setFeedback({ kind: "success", message: successMsg });
      await load();
      onAfterChange();
    } catch (err) {
      const msg =
        err instanceof HttpError
          ? `HTTP ${err.status}${
              (err.body as { message?: string })?.message
                ? `: ${(err.body as { message?: string }).message}`
                : ""
            }`
          : "Acción falló";
      setFeedback({ kind: "error", message: msg });
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div style={{ color: "#73879C", fontSize: ".85rem", padding: "0.4rem 0" }}>
        Cargando biometría…
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          color: "#b32424",
          background: "#fde2e2",
          padding: "0.55rem 0.85rem",
          borderRadius: "0.5rem",
          fontSize: ".85rem",
          marginBottom: "0.5rem",
        }}
      >
        Biometría: {error}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        style={{
          background: "white",
          border: "1px solid #e9ecef",
          borderLeft: "3px solid #0069d9",
          borderRadius: "0.55rem",
          padding: "0.7rem 0.9rem",
          fontSize: ".85rem",
          color: "#73879C",
          marginBottom: "0.5rem",
        }}
      >
        Sin solicitudes de biometría para este lead.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      <div
        style={{
          background: "white",
          border: "1px solid #e9ecef",
          borderLeft: "3px solid #0069d9",
          borderRadius: "0.55rem",
          padding: "0.55rem 0.85rem",
        }}
      >
        <h4
          style={{
            margin: "0 0 0.5rem",
            fontSize: ".7rem",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "#0069d9",
            letterSpacing: ".5px",
          }}
        >
          Biometría
        </h4>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: ".82rem",
          }}
        >
          <thead>
            <tr style={{ color: "#73879C", fontSize: ".7rem" }}>
              <th style={cell}>Status</th>
              <th style={cell}>Documento</th>
              <th style={{ ...cell, textAlign: "right" }}>Calidad</th>
              <th style={cell}>Solicitud</th>
              <th style={{ ...cell, textAlign: "right" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const style = STATUS_STYLES[row.status] ?? {
                bg: "#e9ecef",
                fg: "#495057",
                label: row.status,
              };
              const points = row.request_points ?? 0;
              const passing = points >= 80;
              const syncBusy =
                busy?.kind === "sync" && busy.requestId === row.request_id;
              const acceptBusy =
                busy?.kind === "accept" && busy.requestId === row.request_id;
              return (
                <tr
                  key={row.request_id}
                  style={{ borderTop: "1px solid #f1f3f5" }}
                >
                  <td style={cell}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "0.1rem 0.45rem",
                        borderRadius: "999px",
                        fontSize: ".68rem",
                        fontWeight: 700,
                        background: style.bg,
                        color: style.fg,
                      }}
                    >
                      {style.label}
                    </span>
                  </td>
                  <td style={cell}>{row.document_type}</td>
                  <td
                    style={{
                      ...cell,
                      textAlign: "right",
                      color: passing ? "#1a7f37" : "#b32424",
                      fontWeight: 600,
                      fontFamily: "monospace",
                    }}
                  >
                    {points}%
                  </td>
                  <td style={{ ...cell, color: "#73879C" }}>
                    {row.request_biometry_url ? (
                      <a
                        href={row.request_biometry_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#0069d9", textDecoration: "none" }}
                        title={row.request_id}
                      >
                        {fmtDateTime(row.request_created_at)}
                      </a>
                    ) : (
                      fmtDateTime(row.request_created_at)
                    )}
                  </td>
                  <td style={{ ...cell, textAlign: "right" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: "0.35rem",
                        justifyContent: "flex-end",
                      }}
                    >
                      {row.status === "CREATED" && (
                        <button
                          type="button"
                          disabled={!!busy}
                          onClick={() =>
                            runAction(
                              "sync",
                              row.request_id,
                              "Sincronización lanzada — la tabla se refrescará en un momento",
                            )
                          }
                          style={primaryBtn}
                        >
                          {syncBusy ? "Sync…" : "Sync"}
                        </button>
                      )}
                      {row.status === "CHECKED" && (
                        <button
                          type="button"
                          disabled={!!busy}
                          onClick={() =>
                            runAction(
                              "accept",
                              row.request_id,
                              "Biometría aceptada manualmente",
                            )
                          }
                          style={primaryBtn}
                        >
                          {acceptBusy ? "Aceptando…" : "Aceptar"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {feedback && (
        <div
          onClick={() => setFeedback(null)}
          title="Cerrar"
          style={{
            padding: "0.45rem 0.7rem",
            borderRadius: "0.4rem",
            background: feedback.kind === "success" ? "#d4edda" : "#fde2e2",
            color: feedback.kind === "success" ? "#155724" : "#b32424",
            fontSize: ".78rem",
            cursor: "pointer",
          }}
        >
          {feedback.message}
        </div>
      )}
    </div>
  );
}

const cell: React.CSSProperties = {
  padding: "0.35rem 0.4rem",
  textAlign: "left",
  verticalAlign: "middle",
};

const primaryBtn: React.CSSProperties = {
  padding: "0.25rem 0.6rem",
  borderRadius: "0.35rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".72rem",
  fontWeight: 600,
  cursor: "pointer",
};
